import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { loadAPI, customerAPI, carrierAPI, facilityAPI, interactionAPI, fuelPriceAPI, crossBorderAPI } from '../api';
import { UpdatesDialog } from '../components/load-workspace/UpdatesDialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import { LoadDocumentsDialog } from '../components/load-workspace/LoadDocumentsDialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { toastApiError } from '../lib/utils';
import BOLGenerator from '../components/BOLGenerator';
import { LoadInfoPanel } from '../components/load-workspace/LoadInfoPanel';
import { StopPanel } from '../components/load-workspace/StopPanel';
import { CarrierPanel } from '../components/load-workspace/CarrierPanel';
import { FinancialsPanel } from '../components/load-workspace/FinancialsPanel';
import { DocsCrossBorderPanel } from '../components/load-workspace/DocsCrossBorderPanel';
import { RateConfirmationDialog } from '../components/load-workspace/RateConfirmationDialog';
import { deriveLoadStatus } from '../components/load-workspace/StatusBar';
import '../components/load-workspace/workspace.css';

const FSC_OVERRIDE_ROLES = ['super_admin', 'admin', 'account_manager', 'operator', 'support_operator'];

const LoadWorkspace = () => {
  const { id } = useParams();
  useEffect(() => { if (id) sessionStorage.setItem('tms_last_load_id', id); }, [id]);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [load, setLoad] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [accessorials, setAccessorials] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pickupIdx, setPickupIdx] = useState(0);
  const [deliveryIdx, setDeliveryIdx] = useState(0);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesStopIdx, setNotesStopIdx] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [docsOpen, setDocsOpen] = useState(false);
  const [rcOpen, setRcOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [fscManualOverride, setFscManualOverride] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [dupModal, setDupModal] = useState({ open: false, newId: null, loadNumber: '' });
  const [updatesOpen, setUpdatesOpen] = useState(false);
  const [loadDocsOpen, setLoadDocsOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, reason: '' });

  const makeSnapshot = (l, accs) => JSON.stringify({ l, accs });
  const isDirty = load ? makeSnapshot(load, accessorials) !== savedSnapshot : false;

  // Unsaved-changes warning: browser close/refresh + in-app link navigation
  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    const onLinkClick = (e) => {
      const anchor = e.target.closest && e.target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href') || '';
      if (href.startsWith('/') && !window.confirm('You have unsaved changes. Leave without saving?')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onLinkClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onLinkClick, true);
    };
  }, [isDirty]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [loadRes, custRes, carrRes, facRes] = await Promise.all([
          loadAPI.getById(id),
          customerAPI.getAll(),
          carrierAPI.getAll(),
          facilityAPI.getAll(),
        ]);
        const l = loadRes.data;
        const normalized = { ...l, stops: l.stops || [], crossing_docs: l.crossing_docs || {} };
        const accs = (l.local_accessorials || []).map(a => ({ ...a, _key: a._key || crypto.randomUUID() }));
        setLoad(normalized);
        setAccessorials(accs);
        setSavedSnapshot(JSON.stringify({ l: normalized, accs }));
        setFscManualOverride(!!l.fsc_override);
        setCustomers(custRes.data);
        setCarriers(carrRes.data);
        setFacilities(facRes.data);
        try {
          const intRes = await interactionAPI.getByEntity('load', id);
          setInteractions(intRes.data || []);
        } catch (e) { console.warn('Failed to fetch load interactions:', e?.message); }
      } catch (e) {
        toast.error('Failed to load');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [id]);

  const shipDateISO = useMemo(() => {
    if (!load) return null;
    const firstPickup = (load.stops || []).find(s => s.type === 'pickup' && s.appointment_datetime);
    const raw = firstPickup?.appointment_datetime || load.shipping_date;
    if (!raw) return null;
    try { return new Date(raw).toISOString().slice(0, 10); } catch { return null; }
  }, [load]);

  // FSC auto-populate (same behavior as legacy detail page)
  useEffect(() => {
    if (!load?.customer_id || !shipDateISO || fscManualOverride || load?.fsc_override) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fuelPriceAPI.lookupFSC(load.customer_id, null, shipDateISO);
        const data = res.data;
        if (cancelled || data.fsc_value === null || data.fsc_value === undefined) return;
        setLoad(prev => {
          if (!prev) return prev;
          const customerLineHaul = parseFloat(prev.customer_line_haul) || 0;
          const customerMileage = parseFloat(prev.customer_mileage) || 0;
          const updates = { fsc_enabled: true, fsc_override: false, fsc_fuel_price: data.fuel_price ?? null, fsc_base_price: data.base_price ?? null, fsc_price_date: data.price_date ?? null };
          if (data.table_type === 'percentage') {
            updates.fsc_percentage = data.fsc_value;
            updates.fsc_rpm = null;
            updates.fsc_amount = customerLineHaul ? Math.round(customerLineHaul * data.fsc_value / 100 * 100) / 100 : 0;
          } else {
            updates.fsc_rpm = data.fsc_value;
            updates.fsc_percentage = null;
            updates.fsc_amount = customerMileage ? Math.round(customerMileage * data.fsc_value * 100) / 100 : 0;
          }
          return { ...prev, ...updates };
        });
      } catch (e) { console.debug('No FSC config for customer:', e?.message); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load?.customer_id, shipDateISO, load?.customer_line_haul, load?.customer_mileage, fscManualOverride]);

  const handleChange = (field, value) => {
    setLoad(prev => {
      const next = { ...prev, [field]: value };
      // Assigning / removing the carrier flips Available <-> Booked before any pickup arrival
      if (field === 'carrier_id' && ['Available', 'Booked'].includes(prev.status || 'Available')) next.status = deriveLoadStatus(next);
      // Per Mile: Customer Line Haul = LHM × Customer Miles
      if (['customer_rpm', 'customer_mileage', 'line_haul_type'].includes(field) && next.line_haul_type === 'rpm') {
        next.customer_line_haul = Math.round((parseFloat(next.customer_rpm) || 0) * (parseFloat(next.customer_mileage) || 0) * 100) / 100;
      }
      return next;
    });
  };

  // Server-side actions (border milestones, dispatch) return the saved load: merge their fields without touching unsaved edits
  const SERVER_FIELDS = ['cb', 'activity', 'crossings', 'cb_status', 'cb_status_changed_at', 'cb_stage_override', 'cb_stage_source', 'cb_override_history',
    'crossing_docs', 'crossing_doc_states', 'status', 'status_changed_at', 'dispatched_at', 'dispatched_by', 'dispatched_by_name', 'updated_at'];
  const mergeServerLoad = (serverLoad) => {
    const picked = Object.fromEntries(SERVER_FIELDS.filter(k => k in serverLoad).map(k => [k, serverLoad[k]]));
    setLoad(prev => ({ ...prev, ...picked }));
    setSavedSnapshot(prev => {
      try { const snap = JSON.parse(prev); return JSON.stringify({ ...snap, l: { ...snap.l, ...picked } }); } catch { return prev; }
    });
  };

  const handleDispatch = async () => {
    try {
      const res = await crossBorderAPI.dispatch(id);
      mergeServerLoad(res.data);
      toast.success('Load marked Dispatched');
    } catch (e) { toastApiError(toast, e, 'Could not mark Dispatched'); }
  };

  // Swap a stop with its neighbour of the same type (pickup/delivery), keeping all data; renumber after
  const handleMoveStop = (index, direction) => {
    setLoad(prev => {
      const stops = [...(prev.stops || [])];
      const sameType = stops.map((s, i) => (s.type === stops[index]?.type ? i : -1)).filter(i => i >= 0);
      const target = sameType[sameType.indexOf(index) + direction];
      if (target === undefined) return prev;
      [stops[index], stops[target]] = [stops[target], stops[index]];
      return { ...prev, stops: stops.map((s, i) => ({ ...s, stop_number: i + 1 })) };
    });
  };

  const handleUpdateStop = (index, field, value) => {
    setLoad(prev => {
      const stops = [...(prev.stops || [])];
      stops[index] = { ...stops[index], [field]: value };
      const next = { ...prev, stops };
      // Load Status follows arrival/departure timestamps (backend enforces the same rule on save)
      if ((field === 'actual_arrival' || field === 'actual_departure') && prev.status !== 'Released') next.status = deriveLoadStatus(next);
      return next;
    });
  };

  const handleUpdateAccessorial = (index, field, value) => {
    setAccessorials(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddAccessorial = () => {
    setAccessorials(prev => [...prev, { _key: crypto.randomUUID(), description: '', carrier_amount: 0, customer_amount: 0 }]);
  };

  const handleRemoveAccessorial = (index) => {
    setAccessorials(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!load.customer_id) {
      toast.error('Select a customer before saving');
      return;
    }
    const emptyAcc = accessorials.findIndex(a => !(a.description || '').trim());
    if (emptyAcc !== -1) {
      toast.error(`Accessorial ${emptyAcc + 1} needs a description — select a code or remove the row`);
      return;
    }
    setSaving(true);
    try {
      const enrichedStops = (load.stops || []).map(stop => {
        if (stop.facility_id) {
          const f = facilities.find(x => x.id === stop.facility_id);
          if (f) {
            return {
              ...stop,
              facility_name: stop.facility_name || f.name || '',
              address: stop.address || f.address || '',
              city: stop.city || f.city || '',
              state: stop.state || f.state || '',
              zip_code: stop.zip_code || f.zip_code || '',
            };
          }
        }
        return stop;
      });
      const res = await loadAPI.update(id, { ...load, stops: enrichedStops, local_accessorials: accessorials.map(({ _key, ...rest }) => rest) });
      const l = res.data;
      const normalized = { ...l, stops: l.stops || [], crossing_docs: l.crossing_docs || {} };
      const accs = (l.local_accessorials || []).map(a => ({ ...a, _key: a._key || crypto.randomUUID() }));
      setLoad(normalized);
      setAccessorials(accs);
      setSavedSnapshot(JSON.stringify({ l: normalized, accs }));
      toast.success('Load saved');
      if ((enrichedStops || []).length < 2) toast.warning('Reminder: add at least one pickup and one delivery');
    } catch (error) {
      toastApiError(toast, error, 'Load could not be saved');
    } finally {
      setSaving(false);
    }
  };

  const handleAddStop = (type) => {
    setLoad(prev => {
      const stops = [...(prev.stops || [])];
      stops.push({
        stop_number: stops.length + 1,
        type,
        facility_id: '',
        appointment_status: 'Needs Scheduling',
        references: [],
        appt_screenshots: [],
      });
      return { ...prev, stops };
    });
    toast.success(`New ${type} added — select a facility and Save`);
  };

  const handleRemoveStop = (index) => {
    setLoad(prev => {
      const stops = [...(prev.stops || [])];
      stops.splice(index, 1);
      // Renumber stop_number sequentially
      const renumbered = stops.map((s, i) => ({ ...s, stop_number: i + 1 }));
      return { ...prev, stops: renumbered };
    });
    setPickupIdx(0);
    setDeliveryIdx(0);
    toast.success('Stop removed — remember to Save');
  };

  const handleDuplicate = async () => {
    try {
      const { id: _id, load_number, created_at, updated_at, status_changed_at, ...rest } = load;
      // Strip all date fields per spec ("duplicate everything but dates")
      const cleanedStops = (load.stops || []).map(s => ({
        ...s,
        appointment_datetime: null,
        appointment_end_time: null,
        actual_arrival: null,
        actual_departure: null,
        appointment_status: 'Needs Scheduling',
        appt_screenshots: [],
      }));
      const cleaned = {
        ...rest,
        stops: cleanedStops,
        status: 'Available',
        shipping_date: null,
        delivery_date: null,
        crossing_docs_received_date: null,
        crossing_docs_due_date: null,
        crossing_eta_date: null,
        border_arrival_date_mx: null,
        border_arrival_date_us: null,
        trailer_dropped_date: null,
        trailer_ready_for_pickup_date: null,
        trailer_pulled_date: null,
        local_accessorials: accessorials.map(({ _key, ...rest }) => rest),
      };
      const res = await loadAPI.create(cleaned);
      setDupModal({ open: true, newId: res.data.id, loadNumber: '' });
    } catch (error) {
      toastApiError(toast, error, 'Load could not be duplicated');
    }
  };

  const confirmDuplicateLoadNumber = async () => {
    const { newId, loadNumber } = dupModal;
    try {
      if (loadNumber.trim()) {
        await loadAPI.update(newId, { load_number: loadNumber.trim() });
      }
      setDupModal({ open: false, newId: null, loadNumber: '' });
      toast.success('Load duplicated');
      navigate(`/loads/${newId}`);
    } catch (error) {
      toastApiError(toast, error, 'Load # could not be set');
    }
  };

  // Build the share text per spec
  const fmtApptDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${d.getMonth() + 1}/${d.getDate()} @ ${hh}${mm}`;
    } catch { return ''; }
  };

  // Send Info: email carrier info (Name, MC#, Driver, Truck, Trailer) — Pickup only
  const handleSendInfo = (stop) => {
    const carrier = carriers.find(c => c.id === load.carrier_id);
    if (!carrier && !load.carrier_name) {
      toast.error('Assign a carrier first');
      return;
    }
    const facility = facilities.find(f => f.id === stop?.facility_id);
    const to = facility?.scheduling_email || '';
    const subject = `Carrier Info - Load# ${load.load_number || ''}`;
    const bodyLines = [
      `Carrier: ${carrier?.name || load.carrier_name || ''}`,
      `MC#: ${carrier?.mc_number || ''}`,
      `Driver: ${load.driver_name || ''}`,
      `Truck: ${load.truck_number || ''}`,
      `Trailer: ${load.trailer_number || ''}`,
    ];
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join('\n'))}`;
  };

  const handleDelete = () => setDeleteModal({ open: true, reason: '' });

  const confirmDelete = async () => {
    const reason = deleteModal.reason.trim();
    if (!reason) return;
    try {
      await loadAPI.delete(id, reason);
      setDeleteModal({ open: false, reason: '' });
      toast.success('Load deleted');
      navigate('/loads');
    } catch (e) {
      toast.error('Failed to delete load');
    }
  };

  const handleShare = () => {
    const stops = load.stops || [];
    const pickupsArr = stops.filter(s => s.type === 'pickup');
    const deliveriesArr = stops.filter(s => s.type === 'delivery');
    const firstPU = pickupsArr[0];
    const lastDel = deliveriesArr[deliveriesArr.length - 1];
    const facOf = (s) => facilities.find(f => f.id === s?.facility_id);

    const cityState = (s) => {
      const f = facOf(s);
      const city = s?.city || f?.city || '';
      const st = s?.state || f?.state || '';
      return [city, st].filter(Boolean).join(', ');
    };

    const lines = [];
    lines.push(`${cityState(firstPU)} to ${cityState(lastDel)}`);
    lines.push(`${pickupsArr.length} Pick - ${deliveriesArr.length} Drop`);
    const specs = [];
    if (load.weight) {
      const w = String(load.weight).trim();
      specs.push(/[a-zA-Z]/.test(w) ? w : `${w} lbs`);
    }
    if (load.commodity) specs.push(load.commodity);
    if (load.temperature) specs.push(`temperature ${load.temperature}${load.reefer_mode ? ` ${load.reefer_mode}` : ''}`);
    if (specs.length) lines.push(specs.join(', '));
    lines.push('');
    if (firstPU?.appointment_datetime) {
      lines.push(`Pickup ${fmtApptDate(firstPU.appointment_datetime)}`);
    } else {
      lines.push('Pickup TBD');
    }
    lines.push(`${deliveriesArr.length} ${deliveriesArr.length === 1 ? 'Delivery' : 'Deliveries'}`);
    deliveriesArr.forEach(s => {
      const f = facOf(s);
      const city = s.city || f?.city || '';
      const st = s.state || f?.state || '';
      const zip = s.zip_code || f?.zip_code || '';
      const fname = s.facility_name || f?.name || '';
      const when = s.appointment_datetime ? ` ${fmtApptDate(s.appointment_datetime).replace(' @ ', ' ')}` : '';
      lines.push(`${[city, st].filter(Boolean).join(', ')}${zip ? `  ${zip}` : ''}${fname ? ` - ${fname}` : ''}${when}`);
    });
    lines.push('');
    if (load.rate_to_cover_min) {
      lines.push(`$${Number(load.rate_to_cover_min).toLocaleString()}`);
    }

    const text = lines.join('\n');
    try {
      navigator.clipboard.writeText(text).then(
        () => toast.success('Load summary copied to clipboard'),
        () => { window.prompt('Copy the load summary:', text); }
      );
    } catch {
      window.prompt('Copy the load summary:', text);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    await handleSaveNote(newNote.trim(), notesStopIdx);
    setNewNote('');
  };

  const openNotes = (stopIdx = null) => {
    setNotesStopIdx(stopIdx);
    setNotesOpen(true);
  };

  const handleSaveNote = async (text, stopIdx = null) => {
    try {
      await interactionAPI.create({
        entity_type: 'load',
        entity_id: id,
        interaction_type: 'note',
        subject: stopIdx === null ? 'Load Note' : `Stop ${stopIdx + 1} Note`,
        content: text,
        attachments: [],
        ...(stopIdx === null ? {} : { stop_index: stopIdx }),
      });
      const res = await interactionAPI.getByEntity('load', id);
      setInteractions(res.data || []);
      toast.success('Note saved');
    } catch {
      toast.error('Failed to save note');
      throw new Error('note');
    }
  };

  // Check call saved from a stop panel: update the stop + saved snapshot (does not dirty the form)
  const handleCheckCallSaved = async (index, rec) => {
    const patch = { last_check_call: rec, check_call_count: rec.check_call_count };
    setLoad(prev => {
      const stops = [...(prev.stops || [])];
      stops[index] = { ...stops[index], ...patch };
      return { ...prev, stops };
    });
    setSavedSnapshot(prev => {
      try {
        const s = JSON.parse(prev);
        if (s.l?.stops?.[index]) s.l.stops[index] = { ...s.l.stops[index], ...patch };
        return JSON.stringify(s);
      } catch { return prev; }
    });
    try {
      const res = await interactionAPI.getByEntity('load', id);
      setInteractions(res.data || []);
    } catch (e) { console.debug('refresh notes failed', e?.message); }
  };

  if (loading || !load) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-sm text-gray-500">Loading load workspace...</div>
        </div>
      </MainLayout>
    );
  }

  const pickups = (load.stops || []).map((stop, index) => ({ stop, index })).filter(x => x.stop.type === 'pickup');
  const deliveries = (load.stops || []).map((stop, index) => ({ stop, index })).filter(x => x.stop.type === 'delivery');
  const lastNote = interactions.find(n => n.stop_index === null || n.stop_index === undefined) || null;
  const stopLastNote = (idx) => interactions.find(n => n.stop_index === idx) || null;
  const stopLabel = (idx) => {
    const s = (load.stops || [])[idx];
    return s ? `${s.type === 'pickup' ? 'Pickup' : 'Stop'} ${idx + 1}${s.facility_name ? ` — ${s.facility_name}` : ''}` : `Stop ${idx + 1}`;
  };
  const modalNotes = notesStopIdx === null
    ? interactions.filter(n => n.stop_index === null || n.stop_index === undefined)
    : interactions.filter(n => n.stop_index === notesStopIdx);

  return (
    <MainLayout>
      <div className="lw-root" style={{ minHeight: '100%' }}>
        {/* Header */}
        <div className="lw-header" data-testid="lw-header">
          <div>
            <div className="lw-row" style={{ marginBottom: 2 }}>
              <Link to="/loads" className="lw-link" style={{ fontSize: 12, textDecoration: 'none' }}>← Loads</Link>
              <span style={{ fontSize: 19, fontWeight: 800 }}>Load#</span>
              <input
                className="lw-input"
                style={{ fontSize: 17, fontWeight: 800, maxWidth: 140, height: 30 }}
                value={load.load_number || ''}
                onChange={(e) => handleChange('load_number', e.target.value)}
                placeholder="Load #"
                data-testid="lw-load-number" data-field="load_number"
              />
            </div>
            <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                className="lw-link"
                onClick={() => load.customer_id && navigate(`/customers/${load.customer_id}`)}
                title={load.customer_id ? 'Open customer profile' : 'Select a customer'}
                data-testid="lw-customer-link"
              >
                Customer:
              </span>
              <SearchableSelect
                options={customers.map(c => ({ value: c.id, label: c.name }))}
                value={load.customer_id || ''}
                onValueChange={(val) => {
                  const c = customers.find(x => x.id === val);
                  handleChange('customer_id', val);
                  handleChange('customer_name', c?.name || '');
                  setFscManualOverride(false);
                  handleChange('fsc_override', false);
                }}
                placeholder="Select customer..."
                className="h-7 text-sm min-w-[260px] font-bold text-[var(--lw-red)]"
                data-testid="lw-customer-select" data-field="customer_id"
              />
            </div>
          </div>
          <div style={{ flex: 1, maxWidth: 640 }}>
            <div style={{ fontSize: 13, textAlign: 'right', marginBottom: 4 }}>
              Operator:{' '}
              <span className="lw-link" data-testid="lw-operator-link">{load.operator_name || user?.name || '—'}</span>
            </div>
          </div>
        </div>

        {/* 3x2 grid */}
        <div className="lw-grid">
          <LoadInfoPanel
            load={load}
            onChange={handleChange}
            onDispatch={handleDispatch}
            onSave={handleSave}
            onDuplicate={handleDuplicate}
            onShare={handleShare}
            onDelete={handleDelete}
            onUpdates={() => setUpdatesOpen(true)}
            onDocuments={() => setLoadDocsOpen(true)}
            isDirty={isDirty}
            lastNote={lastNote}
            onOpenNotes={() => openNotes(null)}
            onSaveNote={(text) => handleSaveNote(text, null)}
          />
          <StopPanel
            title="Pickup"
            stops={pickups}
            facilities={facilities}
            loadId={id}
            load={load}
            onUpdateStop={handleUpdateStop}
            onAddStop={handleAddStop}
            onRemoveStop={handleRemoveStop}
            onMoveStop={handleMoveStop}
            onOpenNotes={openNotes}
            onSendInfo={handleSendInfo}
            onSaveNote={handleSaveNote}
            onCheckCallSaved={handleCheckCallSaved}
            currentIdx={Math.min(pickupIdx, Math.max(pickups.length - 1, 0))}
            onSwitch={setPickupIdx}
            lastNote={stopLastNote(pickups[Math.min(pickupIdx, Math.max(pickups.length - 1, 0))]?.index)}
          />
          <StopPanel
            title="Stop"
            stops={deliveries}
            facilities={facilities}
            loadId={id}
            load={load}
            onUpdateStop={handleUpdateStop}
            onAddStop={handleAddStop}
            onRemoveStop={handleRemoveStop}
            onMoveStop={handleMoveStop}
            onOpenNotes={openNotes}
            onSaveNote={handleSaveNote}
            onCheckCallSaved={handleCheckCallSaved}
            currentIdx={Math.min(deliveryIdx, Math.max(deliveries.length - 1, 0))}
            onSwitch={setDeliveryIdx}
            lastNote={stopLastNote(deliveries[Math.min(deliveryIdx, Math.max(deliveries.length - 1, 0))]?.index)}
          />
          <CarrierPanel load={load} carriers={carriers} onChange={handleChange} onCarrierCreated={(c) => setCarriers(prev => [...prev, c])} />
          <FinancialsPanel
            load={load}
            accessorials={accessorials}
            onChange={(field, value) => {
              if (field.startsWith('fsc_')) { setFscManualOverride(true); handleChange('fsc_override', true); }
              handleChange(field, value);
            }}
            canOverrideFsc={FSC_OVERRIDE_ROLES.includes(user?.role)}
            onRemoveFsc={() => {
              setFscManualOverride(true);
              setLoad(prev => ({ ...prev, fsc_override: true, fsc_enabled: false, fsc_amount: 0 }));
            }}
            onRecalcFsc={() => {
              setFscManualOverride(false);
              setLoad(prev => ({ ...prev, fsc_override: false }));
            }}
            onUpdateAccessorial={handleUpdateAccessorial}
            onAddAccessorial={handleAddAccessorial}
            onRemoveAccessorial={handleRemoveAccessorial}
          />
          <DocsCrossBorderPanel
            load={load}
            onChange={handleChange}
            onServerLoad={mergeServerLoad}
            onCreateBOL={() => setDocsOpen(true)}
            onCreateRC={() => setRcOpen(true)}
          />
        </div>

        <RateConfirmationDialog
          open={rcOpen}
          onOpenChange={setRcOpen}
          loadId={id}
          load={load}
          facilities={facilities}
          carriers={carriers}
          accessorials={accessorials}
          onSaved={(rc_data) => {
            setLoad(prev => ({ ...prev, rc_data }));
            setSavedSnapshot(prev => {
              try { const s = JSON.parse(prev); s.l.rc_data = rc_data; return JSON.stringify(s); } catch { return prev; }
            });
          }}
        />

        {/* Notes modal */}
        <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
          <DialogContent className="max-w-lg" data-testid="lw-notes-modal">
            <DialogHeader>
              <DialogTitle>{notesStopIdx === null ? 'Load Notes' : `Notes — ${stopLabel(notesStopIdx)}`}</DialogTitle>
              <DialogDescription className="sr-only">Notes for this {notesStopIdx === null ? 'load' : 'stop'}</DialogDescription>
            </DialogHeader>
            <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {modalNotes.length === 0 && <p style={{ fontSize: 13, color: '#686868' }}>No notes yet.</p>}
              {modalNotes.map((n, i) => (
                <div key={n.id || i} style={{ border: '1px solid #e3e7ef', borderRadius: 4, padding: '6px 10px' }}>
                  <div style={{ fontSize: 11, color: '#686868', marginBottom: 2 }}>
                    {n.created_by_name || 'User'} — {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                  </div>
                  <div style={{ fontSize: 13 }}>{n.content}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <Textarea
                rows={2}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                className="text-sm"
                data-testid="lw-new-note-input"
              />
              <button className="lw-btn primary" onClick={handleAddNote} disabled={!newNote.trim()} data-testid="lw-add-note-btn">Save Note</button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Duplicate — ask for new Load # */}
        <Dialog open={dupModal.open} onOpenChange={(open) => { if (!open && dupModal.newId) confirmDuplicateLoadNumber(); }}>
          <DialogContent className="max-w-sm" data-testid="lw-duplicate-modal">
            <DialogHeader>
              <DialogTitle>Load Duplicated</DialogTitle>
              <DialogDescription>Enter the Load # for the new load</DialogDescription>
            </DialogHeader>
            <input
              className="lw-input"
              style={{ height: 34, fontSize: 15, fontWeight: 700 }}
              autoFocus
              placeholder="New Load #"
              value={dupModal.loadNumber}
              onChange={(e) => setDupModal(prev => ({ ...prev, loadNumber: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmDuplicateLoadNumber(); }}
              data-testid="lw-duplicate-load-number"
            />
            <button className="lw-btn primary" style={{ height: 32 }} onClick={confirmDuplicateLoadNumber} data-testid="lw-duplicate-confirm">
              Open New Load
            </button>
          </DialogContent>
        </Dialog>

        {/* Updates (audit trail) modal */}
        <UpdatesDialog open={updatesOpen} onOpenChange={setUpdatesOpen} entityType="load" entityId={id} />
        <LoadDocumentsDialog
          open={loadDocsOpen}
          onOpenChange={setLoadDocsOpen}
          loadId={id}
          documents={load.load_documents}
          onChange={(docs) => handleChange('load_documents', docs)}
        />

        {/* Delete — reason required */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => { if (!open) setDeleteModal({ open: false, reason: '' }); }}>
          <DialogContent className="max-w-sm" data-testid="lw-delete-modal">
            <DialogHeader>
              <DialogTitle style={{ fontSize: 15 }}>Why are you deleting this load? (required)</DialogTitle>
              <DialogDescription className="sr-only">Provide a reason to delete this load</DialogDescription>
            </DialogHeader>
            <Textarea
              rows={3}
              autoFocus
              value={deleteModal.reason}
              onChange={(e) => setDeleteModal(prev => ({ ...prev, reason: e.target.value }))}
              className="text-sm"
              data-testid="lw-delete-reason-input"
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
              <button className="lw-btn" style={{ height: 32 }} onClick={() => setDeleteModal({ open: false, reason: '' })} data-testid="lw-delete-cancel">
                Cancel
              </button>
              <button
                className="lw-btn"
                style={{ height: 32, background: '#F8191B', borderColor: '#F8191B', color: '#fff', opacity: deleteModal.reason.trim() ? 1 : 0.5 }}
                disabled={!deleteModal.reason.trim()}
                onClick={confirmDelete}
                data-testid="lw-delete-confirm"
              >
                Delete
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Documents / BOL modal */}
        <Dialog open={docsOpen} onOpenChange={setDocsOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="lw-docs-modal">
            <DialogHeader>
              <DialogTitle>Documents — Bill of Lading</DialogTitle>
              <DialogDescription className="sr-only">Generate the Bill of Lading document</DialogDescription>
            </DialogHeader>
            <BOLGenerator loadId={id} load={load} facilities={facilities} carriers={carriers} />
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default LoadWorkspace;
